import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { supabase } from "../lib/supabaseClient";
import Layout from "../components/Layout";
import { PageHeader, Btn, Modal, Field, Input, Select, EmptyState } from "../components/UI";
import { useAuth } from "../lib/auth";
import { Plus, Pencil, Trash2 } from "lucide-react";

const ROLES = ["super_admin","company_admin","manager","operator"];

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({email:"",name:"",password:"",role:"operator",company_id:"",is_active:true});
  const [err, setErr] = useState("");

  const load = async () => {
    const { data, error } = await supabase.from("users").select("id,email,name,role,company_id,is_active").order("created_at", { ascending: false });
    if (error) { setErr(error.message); return; }
    setItems(data || []);
  };

  useEffect(() => {
    load();
    if (user?.role === "super_admin") {
      supabase.from("companies").select("*").then(({ data }) => setCompanies(data || []));
    }
    // eslint-disable-next-line
  }, [user]);

  const openCreate = () => {
    setEdit(null);
    setForm({email:"",name:"",password:"",role:"operator",company_id:user?.role==="super_admin"?"":user.company_id,is_active:true});
    setErr("");
    setOpen(true);
  };
  const openEdit = (it) => { setEdit(it); setForm({...it, password:""}); setErr(""); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      if (edit) {
        // Plain field updates go straight to Supabase (RLS covers who's allowed)
        const upd = { name: form.name, role: form.role, is_active: form.is_active };
        const { error } = await supabase.from("users").update(upd).eq("id", edit.id);
        if (error) throw new Error(error.message);
        // Password changes for OTHER users still need the backend (service_role
        // is required to update another user's Supabase Auth password). If you
        // need this, add a POST /api/users/{id}/reset-password backend route
        // following the same pattern as create_user in server.py.
      } else {
        // Creating a brand-new login requires the users-create Edge Function
        // (service_role key creates the Supabase Auth user; client-side can
        // never do this).
        await api.post(`/users-create`, form);
      }
      setOpen(false);
      load();
    } catch (e) {
      setErr(e.response?.data?.error || e.message || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete user?")) return;
    const { error } = await supabase.from("users").delete().eq("id", id);
    if (error) { alert(error.message); return; }
    load();
    // Note: this removes the `users` row (and RLS access) but the underlying
    // Supabase Auth account still exists. To fully remove login ability too,
    // add a backend route using supabase.auth.admin.delete_user(id).
  };

  const visibleRoles = user?.role === "super_admin" ? ROLES : ROLES.filter(r => r !== "super_admin");

  return (
    <Layout>
      <PageHeader title="Users" subtitle="Manage system users and their roles"
        actions={<Btn onClick={openCreate} data-testid="user-add"><Plus size={16} className="inline -mt-0.5 mr-1"/>Add User</Btn>}/>
      {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
      {items.length === 0 ? <EmptyState title="No users yet"/> : (
        <div className="lt-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr><th className="text-left px-4 py-2.5">Name</th><th className="text-left">Email</th><th className="text-left">Role</th><th className="text-left">Status</th><th className="text-right px-4">Actions</th></tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} className="border-t hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-medium">{it.name}</td>
                  <td className="text-slate-600">{it.email}</td>
                  <td><span className="px-2 py-0.5 rounded bg-slate-100 text-xs capitalize">{it.role.replace("_"," ")}</span></td>
                  <td>{it.is_active ? <span className="text-emerald-600 text-xs font-medium">Active</span> : <span className="text-red-600 text-xs">Disabled</span>}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={()=>openEdit(it)} className="p-1.5 hover:bg-sky-100 rounded text-sky-700"><Pencil size={15}/></button>
                    <button onClick={()=>del(it.id)} className="p-1.5 hover:bg-red-100 rounded text-red-600 ml-1"><Trash2 size={15}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal open={open} onClose={()=>setOpen(false)} title={edit ? "Edit User" : "New User"} size="md">
        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          <Field label="Name"><Input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></Field>
          <Field label="Email"><Input required type="email" disabled={!!edit} value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></Field>
          {!edit && <Field label="Password"><Input type="password" required value={form.password} onChange={e=>setForm({...form,password:e.target.value})}/></Field>}
          <Field label="Role"><Select value={form.role} onChange={e=>setForm({...form,role:e.target.value})}>{visibleRoles.map(r=><option key={r} value={r}>{r.replace("_"," ")}</option>)}</Select></Field>
          {user?.role==="super_admin" && !edit && (
            <Field label="Company" className="col-span-2"><Select value={form.company_id||""} onChange={e=>setForm({...form,company_id:e.target.value||null})}><option value="">— Platform (no company) —</option>{companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
          )}
          <Field label="Status" className="col-span-2"><Select value={form.is_active?"1":"0"} onChange={e=>setForm({...form,is_active:e.target.value==="1"})}><option value="1">Active</option><option value="0">Disabled</option></Select></Field>
          {err && <div className="col-span-2 text-sm text-red-600">{err}</div>}
          <div className="col-span-2 flex justify-end gap-2"><Btn variant="ghost" type="button" onClick={()=>setOpen(false)}>Cancel</Btn><Btn type="submit" data-testid="user-save">{edit?"Update":"Create"}</Btn></div>
        </form>
      </Modal>
    </Layout>
  );
}
